import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import {
  addNativeVoipTokenListener,
  getNativeVoipToken,
  noteRegisteredVoipToken,
  registerNativeTelecomAccount,
} from '../services/voice-call/native-call'
import { registerPushToken } from '../services/appsync-client'
import { pushDiagnostics } from '../stores/push-diagnostics-store'

/**
 * iOS only: register the PushKit VoIP token (CallKit) as the IOS_VOIP push
 * platform so the backend delivers call invites as VoIP pushes. The standard
 * APNs token (IOS) is still registered by usePushNotifications for messages.
 *
 * No-op on Android/web. iOS requires the Push Notifications + Background Modes
 * (voip) capabilities and a VoIP-capable APNs key in Firebase — see
 * frontend/XCODE.md § CallKit / PushKit.
 *
 * Reliability notes (why this is shaped the way it is):
 *  - The PushKit token can arrive EITHER from the initial read or via the
 *    listener, and the listener is attached BEFORE the initial read so a token
 *    that lands during startup is never missed (previously a race could drop
 *    it, leaving ZERO IOS_VOIP tokens in the push-tokens table → no CallKit
 *    ring).
 *  - Registration is de-duped per token value, so the initial read + listener
 *    can both fire without double-registering, but a genuine token *refresh*
 *    still re-registers.
 *  - Failures are NO LONGER swallowed silently: each outcome is logged through
 *    the push-diagnostics store (visible on the Profile page) and console so a
 *    missing token / failed backend write is diagnosable on-device.
 */
export function useIosVoipPush(userId: string | undefined): void {
  useEffect(() => {
    // Always breadcrumb the gating decision itself — visible on the Profile
    // page diagnostics without any device console access. If this line never
    // shows up on a real-device test, the hook effect isn't firing at all
    // (mount/route issue upstream, not PushKit/native).
    if (!userId) {
      pushDiagnostics.note(`IOS_VOIP: hook effect ran but skipped — no userId yet`)
      return
    }
    if (Capacitor.getPlatform() !== 'ios') {
      pushDiagnostics.note(`IOS_VOIP: hook effect ran but skipped — platform is '${Capacitor.getPlatform()}', not iOS`)
      return
    }
    pushDiagnostics.note('IOS_VOIP: hook effect starting (userId present, platform=ios)')

    let cancelled = false
    let removeListener: (() => void) | null = null
    /** Last token value we successfully sent to the backend (dedupe guard). */
    let registeredToken: string | null = null

    const register = (token: string) => {
      if (cancelled || !token) return
      // Dedupe: the initial read and the listener can both deliver the same
      // token; only a real refresh (new value) should hit the backend again.
      if (token === registeredToken) return
      registeredToken = token
      pushDiagnostics.note(`IOS_VOIP: registering token (…${token.slice(-6)}, len ${token.length})`)
      void registerPushToken(token, 'IOS_VOIP')
        .then(() => {
          noteRegisteredVoipToken(token)
          pushDiagnostics.note('IOS_VOIP: registerPushToken succeeded')
        })
        .catch((error: unknown) => {
          // Reset so a later retry (refresh / remount) can attempt again.
          registeredToken = null
          const message = error instanceof Error ? error.message : String(error)
          pushDiagnostics.note(`IOS_VOIP: registerPushToken FAILED — ${message}`)
          console.warn('[useIosVoipPush] IOS_VOIP token registration failed', error)
        })
    }

    void (async () => {
      // Attach the listener FIRST so a token that arrives during startup (before
      // or during the initial read) is captured — eliminating the prior race.
      removeListener = await addNativeVoipTokenListener(register)
      if (cancelled) {
        removeListener()
        return
      }
      // Start PushKit (idempotent). Safe to call after attaching the listener.
      await registerNativeTelecomAccount()
      if (cancelled) return
      // Pick up a token that was already minted before this mount.
      const token = await getNativeVoipToken()
      if (token) {
        register(token)
      } else {
        pushDiagnostics.note('IOS_VOIP: no token yet (awaiting PushKit registration)')
      }
    })()

    return () => {
      cancelled = true
      removeListener?.()
    }
  }, [userId])
}
